import axios from "axios";
import { showAlert, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker"; // <-- Importado

export default (initialAppointmentId = null) => ({
  isLoading: true,
  isSubmitting: false,
  isSearchingClient: false, // <-- Novo estado para o loading

  // Dependências
  services: [],
  products: [],
  professionals: [],
  paymentMethods: [],

  // Global Session State
  loggedInUserId: null,

  // Estado do Carrinho (Agora com Telefone)
  order: {
    appointment_id: initialAppointmentId,
    customer_phone: "", // <-- Adicionado
    customer_name: "",
    discount: 0,
  },

  // Listas Dinâmicas
  items: [],
  payments: [],

  // Modais
  activeModal: null,
  tempItem: { type: "", id: "", qty: 1, user_id: "" },
  tempPayment: { method_id: "", amount: 0 },

  async initPos() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      const tokenPayload = token.split(".")[1];
      const decodedPayload = JSON.parse(atob(tokenPayload));
      this.loggedInUserId = decodedPayload.sub;
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }

    await this.fetchDependencies();

    // 🛠️ MÁGICA: Se veio de um agendamento, hidrata o carrinho antes de tirar o Loading
    if (this.order.appointment_id) {
      await this.hydrateFromAppointment();
    }

    this.isLoading = false;

    // Inicializa a máscara logo a seguir ao render do Alpine
    this.$nextTick(() => {
      this.initPhoneMask();
    });
  },

  // 🛠️ NOVA FUNÇÃO: Busca os dados e preenche a tela
  async hydrateFromAppointment() {
    try {
      const response = await axios.get(
        `/api/client/appointments/${this.order.appointment_id}/pos-data`,
      );
      const data = response.data.data;

      // Preenche os dados do cliente
      this.order.customer_name = data.customer_name;

      // Formata o telefone caso venha sujo do banco
      if (data.customer_phone) {
        const rawPhone = data.customer_phone.replace(/\D/g, "");
        this.order.customer_phone = rawPhone; // A máscara se ajustará no input
      }

      // Adiciona o serviço ao carrinho
      if (data.item) {
        this.items.push(data.item);
      }
    } catch (error) {
      handleRequestError(error, "Erro ao carregar os dados do agendamento.");
      // Em caso de erro (ex: já pago), limpamos o ID para permitir o uso livre do PDV
      this.order.appointment_id = null;
    }
  },

  initPhoneMask() {
    const phoneInput = document.getElementById("pos_customer_phone_input");
    if (phoneInput) {
      VMasker(phoneInput).maskPattern("(99) 99999-9999");
    }
  },

  // A Busca Inteligente no PDV
  async searchClientByPhone() {
    const rawPhone = this.order.customer_phone.replace(/\D/g, "");

    if (rawPhone.length >= 10) {
      this.isSearchingClient = true;
      try {
        // Usa a rota protegida da API que criámos no Passo 2
        const response = await axios.get("/api/client/clients/find-by-phone", {
          params: { phone: rawPhone },
        });

        if (response.data.data && response.data.data.name) {
          this.order.customer_name = response.data.data.name;
        }
      } catch (error) {
        console.error("Erro ao buscar cliente", error);
      } finally {
        this.isSearchingClient = false;
      }
    }
  },

  async fetchDependencies() {
    try {
      const [resSrv, resPrd, resUsers, resPay] = await Promise.all([
        axios.get("/api/client/services"),
        axios.get("/api/client/products"),
        axios.get("/api/client/appointments/professionals"),
        axios.get("/api/client/payment-methods"),
      ]);
      this.services = resSrv.data.data.filter((s) => s.is_active);
      this.products = resPrd.data.data;
      this.professionals = resUsers.data.data;
      this.paymentMethods = resPay.data.data.filter((p) => p.is_active);
    } catch (error) {
      console.error("Erro ao carregar dependências", error);
    }
  },

  // ==================== MATEMÁTICA ====================
  get grossTotal() {
    return this.items.reduce((sum, item) => sum + item.price * item.qty, 0);
  },

  get netTotal() {
    const total = this.grossTotal - (parseFloat(this.order.discount) || 0);
    return total > 0 ? total : 0;
  },

  get totalPaid() {
    return this.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  },

  get remainingBalance() {
    const balance = this.netTotal - this.totalPaid;
    return balance > 0 ? balance : 0;
  },

  get canSubmit() {
    return (
      this.items.length > 0 && Math.abs(this.netTotal - this.totalPaid) < 0.01
    );
  },

  // ==================== AÇÕES DE ITENS ====================
  openItemModal(type) {
    this.tempItem = {
      type: type,
      id: "",
      qty: 1,
      user_id: this.loggedInUserId ? Number(this.loggedInUserId) : "",
    };

    this.activeModal = type;
  },

  confirmAddItem() {
    if (!this.tempItem.id) return;

    if (
      this.activeModal === "product" &&
      (!this.tempItem.qty || this.tempItem.qty < 1)
    )
      return;

    let isService = this.activeModal === "service";
    let source = isService ? this.services : this.products;
    let selected = source.find((i) => i.id == this.tempItem.id);
    let prof = this.professionals.find((p) => p.id == this.tempItem.user_id);

    this.items.push({
      type: this.activeModal,
      id: selected.id,
      name: selected.name,
      price: selected.price,
      qty: isService ? 1 : parseInt(this.tempItem.qty),
      user_id: prof ? prof.id : null,
      user_name: prof ? prof.name : isService ? "Sem Profissional" : "",
    });

    this.activeModal = null;
    this.autoAdjustPayments();
  },

  removeItem(index) {
    this.items.splice(index, 1);
    this.autoAdjustPayments();
  },

  // ==================== AÇÕES DE PAGAMENTO ====================
  openPaymentModal() {
    this.tempPayment = { method_id: "", amount: this.remainingBalance };
    this.activeModal = "payment";
  },

  confirmAddPayment() {
    if (!this.tempPayment.method_id || this.tempPayment.amount <= 0) return;

    let method = this.paymentMethods.find(
      (m) => m.id == this.tempPayment.method_id,
    );

    this.payments.push({
      method_id: method.id,
      name: method.name,
      amount: parseFloat(this.tempPayment.amount),
    });

    this.activeModal = null;
  },

  removePayment(index) {
    this.payments.splice(index, 1);
  },

  autoAdjustPayments() {
    if (this.totalPaid > this.netTotal) {
      this.payments = [];
    }
  },

  // ==================== FINALIZAR ====================
  async submitSale() {
    if (!this.canSubmit) return;

    // Remove a máscara para verificar se realmente existem números digitados
    const cleanPhone = this.order.customer_phone
      ? this.order.customer_phone.replace(/\D/g, "")
      : "";

    // Validação obrigatória de Nome e Telefone
    if (!this.order.customer_name.trim() || cleanPhone.length === 0) {
      return showAlert(
        "Por favor, informe o WhatsApp e o Nome do cliente antes de finalizar a venda.",
        "warning",
      );
    }

    this.isSubmitting = true;

    const payload = {
      appointment_id: this.order.appointment_id,
      customer_phone: this.order.customer_phone, // <-- Enviando o telefone
      customer_name: this.order.customer_name,
      discount: this.order.discount || 0,
      items: this.items,
      payments: this.payments,
    };

    try {
      // CORRIGIDO PARA service-orders
      await axios.post("/api/client/pos", payload);
      showAlert("Venda Finalizada com Sucesso!", "success");

      this.items = [];
      this.payments = [];
      this.order = {
        appointment_id: null,
        customer_phone: "",
        customer_name: "",
        discount: 0,
      };
    } catch (error) {
      handleRequestError(error, "Erro ao fechar a venda.");
    } finally {
      this.isSubmitting = false;
    }
  },

  formatMoney(value) {
    return parseFloat(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
    });
  },
});
